import {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import i18n from "@/i18n";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";
import {
  normalizeSupportedLanguage,
  type SupportedLanguageCode,
} from "@/lib/language-detection";
import {
  identifyPostHogUser,
  resetPostHog,
  capturePostHogEvent,
  syncPostHogConsent,
} from "@/lib/posthog";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    fullName: string,
  ) => Promise<{ error: Error | null }>;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{
    error: Error | null;
    preferredLanguage: SupportedLanguageCode | null;
  }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const resolveSupportedLanguage = (
  value: unknown,
): SupportedLanguageCode | null => {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  if (
    normalized === "spanish" ||
    normalized === "espanol" ||
    normalized === "español"
  ) {
    return "es";
  }

  if (
    normalized === "english" ||
    normalized === "ingles" ||
    normalized === "inglés"
  ) {
    return "en";
  }

  const prefix = normalized.slice(0, 2);
  if (prefix !== "en" && prefix !== "es") {
    return null;
  }

  return normalizeSupportedLanguage(normalized);
};

const getLanguageFromMetadata = (
  user: User | null,
): SupportedLanguageCode | null => {
  if (!user?.user_metadata || typeof user.user_metadata !== "object") {
    return null;
  }

  const metadata = user.user_metadata as Record<string, unknown>;
  const candidates = [
    metadata.preferred_language,
    metadata.language_preference,
    metadata.language,
  ];

  for (const candidate of candidates) {
    const normalized = resolveSupportedLanguage(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return null;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const languageSyncCacheRef = useRef<Record<string, SupportedLanguageCode>>(
    {},
  );

  const clearSupabaseAuthStorage = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const keys = Object.keys(window.localStorage);
      for (const key of keys) {
        if (
          key === "supabase.auth.token" ||
          (key.startsWith("sb-") && key.endsWith("-auth-token"))
        ) {
          window.localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.error("Failed to clear Supabase auth storage:", error);
    }
  }, []);

  const syncUserPreferences = useCallback(async (currentUser: User | null) => {
    if (!currentUser) {
      syncPostHogConsent();
      return null;
    }

    identifyPostHogUser(currentUser.id, { email: currentUser.email });

    const metadataLanguage = getLanguageFromMetadata(currentUser);
    const cachedLanguage = languageSyncCacheRef.current[currentUser.id];
    let preferredLanguage = metadataLanguage ?? cachedLanguage ?? null;
    let trackingOptOut: boolean | undefined;

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("language_preference, tracking_opt_out")
        .eq("id", currentUser.id)
        .maybeSingle();

      if (!error) {
        const profileLanguage = resolveSupportedLanguage(
          data?.language_preference ?? null,
        );
        if (!preferredLanguage && profileLanguage) {
          preferredLanguage = profileLanguage;
        }
        trackingOptOut = data?.tracking_opt_out ?? undefined;
      }
    } catch {
      // Keep current preferences if profile lookup fails.
    }

    if (preferredLanguage) {
      languageSyncCacheRef.current[currentUser.id] = preferredLanguage;
      if (i18n.language !== preferredLanguage) {
        await i18n.changeLanguage(preferredLanguage);
      }
    }

    syncPostHogConsent(trackingOptOut);
    return preferredLanguage;
  }, []);

  useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      // Update Realtime connection with fresh token to prevent "InvalidJWTToken" errors
      // when the original token expires while WebSocket connections are active
      if (session?.access_token) {
        supabase.realtime.setAuth(session.access_token);
      }

      // Update Sentry user context
      if (session?.user) {
        setSentryUser({
          id: session.user.id,
          email: session.user.email,
        });

        if (event === "SIGNED_IN") {
          capturePostHogEvent("zapido_auth_signed_in");
        }
      } else {
        if (event === "SIGNED_OUT") {
          capturePostHogEvent("zapido_auth_signed_out");
        }
        clearSentryUser();
        resetPostHog();
      }

      void syncUserPreferences(session?.user ?? null).finally(() => {
        setLoading(false);
      });
    });

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      // Set initial Realtime auth token to prevent "InvalidJWTToken" errors
      // This is needed because onAuthStateChange may not fire on initial load
      // if the session is restored from localStorage
      if (session?.access_token) {
        supabase.realtime.setAuth(session.access_token);
      }

      // Set initial Sentry user context
      if (session?.user) {
        setSentryUser({
          id: session.user.id,
          email: session.user.email,
        });
      } else {
        resetPostHog();
      }

      await syncUserPreferences(session?.user ?? null);
      setLoading(false);
    });

    // Proactive token refresh: Check every 1 minute if token is about to expire
    // and refresh it before Realtime connections fail with "InvalidJWTToken" errors.
    // This handles the case where users leave the tab open for extended periods
    // without activity (autoRefreshToken only refreshes on API calls).
    // Note: We use aggressive timing because browser throttling can delay intervals.
    const REFRESH_INTERVAL_MS = 1 * 60 * 1000; // Check every 1 minute (was 2)
    const REFRESH_THRESHOLD_MS = 10 * 60 * 1000; // Refresh 10 minutes before expiry (was 5)

    const proactiveRefreshInterval = setInterval(async () => {
      try {
        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession();

        if (!currentSession?.expires_at) return;

        const expiresAt = currentSession.expires_at * 1000; // Convert to milliseconds
        const now = Date.now();
        const timeUntilExpiry = expiresAt - now;

        // If token expires within threshold, proactively refresh
        if (timeUntilExpiry > 0 && timeUntilExpiry <= REFRESH_THRESHOLD_MS) {
          const { data, error } = await supabase.auth.refreshSession();

          if (error) {
            console.error(
              "Proactive token refresh failed:",
              error instanceof Error ? error.message : "unknown error",
            );
            return;
          }

          if (data.session?.access_token) {
            // Update Realtime with fresh token
            supabase.realtime.setAuth(data.session.access_token);
          }
        }
      } catch (error) {
        console.error(
          "Error in proactive token refresh:",
          error instanceof Error ? error.message : "unknown error",
        );
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      subscription.unsubscribe();
      clearInterval(proactiveRefreshInterval);
    };
  }, [syncUserPreferences]);

  // Refresh token when tab becomes visible (handles backgrounded tabs where
  // intervals are throttled or suspended by the browser)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        try {
          const {
            data: { session: currentSession },
          } = await supabase.auth.getSession();

          if (!currentSession?.expires_at) return;

          const expiresAt = currentSession.expires_at * 1000;
          const timeUntilExpiry = expiresAt - Date.now();

          // Refresh if token expired or expires within 10 minutes
          if (timeUntilExpiry <= 10 * 60 * 1000) {
            const { data, error } = await supabase.auth.refreshSession();

            if (!error && data.session?.access_token) {
              supabase.realtime.setAuth(data.session.access_token);
            }
          }
        } catch (error) {
          console.error(
            "Error refreshing on visibility change:",
            error instanceof Error ? error.message : "unknown error",
          );
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Refresh token when coming back online (handles network reconnection)
  useEffect(() => {
    const handleOnline = async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();

        if (!error && data.session?.access_token) {
          supabase.realtime.setAuth(data.session.access_token);
        }
      } catch (error) {
        console.error(
          "Error refreshing on online:",
          error instanceof Error ? error.message : "unknown error",
        );
      }
    };

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/onboarding`;
    const preferredLanguage =
      localStorage.getItem("i18nextLng") === "es" ? "es" : "en";

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          language: preferredLanguage,
          language_preference: preferredLanguage,
          preferred_language: preferredLanguage,
        },
      },
    });

    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    let preferredLanguage: SupportedLanguageCode | null = null;
    if (!error) {
      preferredLanguage = await syncUserPreferences(data.user ?? null);
    }

    return {
      error: error as Error | null,
      preferredLanguage,
    };
  };

  const signOut = async () => {
    const { error: globalSignOutError } = await supabase.auth.signOut();

    // If global sign-out cannot revoke refresh tokens (for example stale/expired
    // refresh token), still force local session removal so users can always log out.
    if (globalSignOutError) {
      console.warn(
        "Global sign-out failed, attempting local sign-out fallback",
      );
      const { error: localSignOutError } = await supabase.auth.signOut({
        scope: "local",
      });

      if (localSignOutError) {
        console.error("Local sign-out fallback failed:", localSignOutError);
      }
    }

    clearSupabaseAuthStorage();
    setSession(null);
    setUser(null);
    clearSentryUser();
    resetPostHog();
    syncPostHogConsent();
  };

  return (
    <AuthContext.Provider
      value={{ user, session, loading, signUp, signIn, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
